import { useEffect, useState } from 'react'
import { format } from 'date-fns'
import type { UpcomingRenewalsResponse } from 'api/generated'
import { Button } from 'components/ui/button'
import { axisProps, formatPeriod } from 'helpers/daily-report'
import { Loader } from 'lucide-react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { fetchUpcomingRenewals } from 'store/dailyReport/dailyReportSlice'
import { useAppDispatch, useAppSelector } from 'store/store'
import { MetricTile } from './MetricTile'

const DAY_OPTIONS = [7, 14, 30, 90]
const CHARGES_COLOR = 'var(--chart-1)'
const EXPIRING_COLOR = 'var(--chart-2)'

const integer = new Intl.NumberFormat('it-IT')
const euro = new Intl.NumberFormat('it-IT', {
  style: 'currency',
  currency: 'EUR',
})
const n = (value: number | null | undefined) =>
  value == null ? '–' : integer.format(value)

type DayPoint = {
  date: string
  charges: number
  amount: number
  expiring: number
}

const dayLabel = (date: string) =>
  format(new Date(`${date}T00:00:00`), 'dd/MM/yyyy')

const DayTooltip = ({
  active,
  payload,
  withCharges,
}: {
  active?: boolean
  payload?: ReadonlyArray<{ payload?: unknown }>
  withCharges: boolean
}) => {
  const point = payload?.[0]?.payload as DayPoint | undefined
  if (!active || !point) return null
  return (
    <div className='rounded-md border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-sm'>
      <div className='text-muted-foreground'>{dayLabel(point.date)}</div>
      {withCharges && (
        <div className='tabular-nums'>
          Rinnovi programmati: <b>{n(point.charges)}</b> (
          {euro.format(point.amount)})
        </div>
      )}
      <div className='tabular-nums'>
        Premium in scadenza: <b>{n(point.expiring)}</b>
      </div>
    </div>
  )
}

const LegendDot = ({ color, label }: { color: string; label: string }) => (
  <span className='inline-flex items-center gap-1'>
    <span className='h-2 w-2 rounded-sm' style={{ background: color }} />
    {label}
  </span>
)

type ChargeMatch = NonNullable<
  UpcomingRenewalsResponse['recharge']
>['by_user_premium']

/** Why the queued charges and the expiring premium users don't add up. */
const ChargesVsExpiring = ({
  match,
  charges,
  expiring,
  withCharge,
}: {
  match: ChargeMatch
  charges: number
  expiring: number
  withCharge: number | null
}) => {
  const rows = [
    {
      label: 'di utenti che scadono nel periodo',
      value: match.expiring_in_period,
      note: `${n(match.expiring_same_day)} lo stesso giorno dell'addebito`,
    },
    {
      label: 'di utenti con premium oltre il periodo',
      value: match.expiring_after_period,
      note: 'codice riscattato giorni dopo il pagamento o acquisti sommati',
    },
    {
      label: 'di utenti senza premium attivo',
      value: match.not_premium,
      note: 'codice mai riscattato o premium già perso',
    },
    {
      label: 'con email non associata a un utente',
      value: match.no_user,
      note: 'email Shopify diversa da quella dell’app',
    },
  ]
  return (
    <details className='mt-3 rounded-md border bg-muted/40 p-3 text-xs'>
      <summary className='cursor-pointer font-medium'>
        Perché i rinnovi programmati ({n(charges)}) non coincidono con i premium
        in scadenza ({n(expiring)})?
      </summary>
      <p className='mt-2 text-muted-foreground'>
        Sono due conteggi diversi: un addebito Recharge cade nel giorno
        anniversario dell&apos;abbonamento, la scadenza premium è un anno dopo
        il riscatto del codice. Un utente in scadenza conta come rinnovato solo
        se il suo addebito è nello stesso periodo ({n(withCharge)} su{' '}
        {n(expiring)}). Gli addebiti del periodo sono:
      </p>
      <dl className='mt-2 space-y-1'>
        {rows.map((row) => (
          <div
            key={row.label}
            className='flex flex-wrap items-baseline gap-x-2'
          >
            <dt className='font-semibold tabular-nums'>{n(row.value)}</dt>
            <dd>
              {row.label}{' '}
              <span className='text-muted-foreground'>({row.note})</span>
            </dd>
          </div>
        ))}
      </dl>
      <p className='mt-2 text-muted-foreground'>
        Nel grafico per giorno lo scarto è più forte: basta un giorno di
        differenza tra addebito e scadenza per spostare un rinnovo sulla barra
        accanto.
      </p>
    </details>
  )
}

/**
 * Renewals expected from today: charges queued on Recharge (live) and premium
 * memberships expiring in the app, day by day.
 */
export const UpcomingRenewals = () => {
  const dispatch = useAppDispatch()
  const { upcomingRenewals: data, upcomingRenewalsLoading: loading } =
    useAppSelector((state) => state.dailyReport)
  const [days, setDays] = useState(30)

  useEffect(() => {
    dispatch(fetchUpcomingRenewals(days))
  }, [dispatch, days])

  const recharge = data?.recharge
  const membership = data?.membership
  const chargesByDay = new Map(recharge?.by_day.map((d) => [d.date, d]) ?? [])
  const points: DayPoint[] = (membership?.by_day ?? []).map((day) => ({
    date: day.date,
    charges: chargesByDay.get(day.date)?.count ?? 0,
    amount: chargesByDay.get(day.date)?.amount ?? 0,
    expiring: day.count,
  }))

  return (
    <div className='rounded-lg border p-4'>
      <div className='flex flex-wrap items-start justify-between gap-4'>
        <div>
          <h3 className='text-sm font-semibold'>Rinnovi in arrivo</h3>
          <p className='text-xs text-muted-foreground'>
            Addebiti in coda su Recharge e premium che scadono nell&apos;app
            {data && `, ${formatPeriod(data.period.from, data.period.to)}`}
          </p>
        </div>
        <div className='flex flex-wrap items-center gap-1'>
          {DAY_OPTIONS.map((option) => (
            <Button
              key={option}
              size='sm'
              className='h-7 px-2 text-xs'
              variant={days === option ? 'secondary' : 'ghost'}
              onClick={() => setDays(option)}
            >
              {option} giorni
            </Button>
          ))}
        </div>
      </div>

      {loading && !data ? (
        <Loader className='mt-3 h-5 w-5 animate-spin' />
      ) : !data || !membership ? (
        <p className='mt-3 text-sm text-muted-foreground'>
          Nessun dato disponibile.
        </p>
      ) : (
        <div className={loading ? 'opacity-60' : undefined}>
          <div className='mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6'>
            <MetricTile
              label='Rinnovi programmati'
              value={n(recharge?.queued_charges)}
              note='addebiti in coda su Recharge'
            />
            <MetricTile
              label='Importo atteso'
              value={recharge ? euro.format(recharge.amount) : '–'}
              note={recharge?.by_price
                .map((p) => `${euro.format(p.price)} × ${n(p.count)}`)
                .join(' · ')}
            />
            <MetricTile
              label='Premium in scadenza'
              value={n(membership.expiring_premium_users)}
              note={`${n(membership.with_paid_purchase)} hanno pagato almeno una volta`}
            />
            <MetricTile
              label='In scadenza con rinnovo'
              value={n(membership.with_queued_charge)}
              note='addebito in coda con la stessa email'
            />
            <MetricTile
              label='In scadenza senza rinnovo'
              value={n(membership.without_queued_charge)}
              note={
                membership.without_queued_charge_paid != null
                  ? `di cui ${n(membership.without_queued_charge_paid)} paganti`
                  : undefined
              }
              title='Perdono il premium se non riacquistano: abbonamento cancellato, codice omaggio o email Shopify diversa'
            />
            <MetricTile
              label='Solo codici gratuiti'
              value={n(membership.only_free_codes)}
              note='omaggi, prove, codici manuali'
            />
          </div>

          {recharge && (
            <ChargesVsExpiring
              match={recharge.by_user_premium}
              charges={recharge.queued_charges}
              expiring={membership.expiring_premium_users}
              withCharge={membership.with_queued_charge}
            />
          )}

          <div className='mt-3 flex gap-3 text-xs text-muted-foreground'>
            {recharge && (
              <LegendDot color={CHARGES_COLOR} label='Rinnovi programmati' />
            )}
            <LegendDot color={EXPIRING_COLOR} label='Premium in scadenza' />
          </div>
          <div className='mt-2 h-[220px]'>
            <ResponsiveContainer width='100%' height='100%'>
              <BarChart
                data={points}
                margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
                barGap={2}
              >
                <CartesianGrid
                  vertical={false}
                  stroke='var(--border)'
                  strokeDasharray='3 3'
                />
                <XAxis
                  dataKey='date'
                  tickFormatter={(d: string) => dayLabel(d).slice(0, 5)}
                  minTickGap={16}
                  {...axisProps}
                />
                <YAxis
                  allowDecimals={false}
                  width={48}
                  domain={[0, 'auto']}
                  {...axisProps}
                />
                <Tooltip
                  cursor={{ fill: 'var(--muted)' }}
                  content={({ active, payload }) => (
                    <DayTooltip
                      active={active}
                      payload={payload}
                      withCharges={!!recharge}
                    />
                  )}
                />
                {recharge && (
                  <Bar
                    dataKey='charges'
                    fill={CHARGES_COLOR}
                    radius={[4, 4, 0, 0]}
                    maxBarSize={20}
                    isAnimationActive={false}
                  />
                )}
                <Bar
                  dataKey='expiring'
                  fill={EXPIRING_COLOR}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={20}
                  isAnimationActive={false}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p className='mt-2 text-xs text-muted-foreground'>
            {recharge
              ? 'Gli addebiti in coda sono un massimo: fino al giorno del rinnovo un abbonato può cancellare, saltare o non riuscire a pagare. Utenti e addebiti sono abbinati per email (account o acquisto).'
              : 'Recharge non disponibile in questo momento: solo le scadenze premium dell’app.'}
          </p>
        </div>
      )}
    </div>
  )
}
